<template>
	<div class="bg-white p-4">
		<div class="flex justify-between items-center mb-4">
			<h6 class="text-lg uppercase tracking-wider text-gray-500">{{ title }}</h6>
			<v-btn color="teal" class="uppercase" dark @click="openAdd">
				<v-icon left>mdi-account-plus</v-icon>
				Adauga persoana
			</v-btn>
		</div>

		<v-simple-table>
			<thead>
				<tr>
					<th class="w-12">Nr.</th>
					<th>Nume</th>
					<th>Culoare calendar</th>
					<th class="w-12"></th>
				</tr>
			</thead>
			<tbody>
				<tr v-if="loadingPeople && !people.length">
					<td colspan="4"><v-progress-linear indeterminate /></td>
				</tr>
				<tr v-else-if="!people.length">
					<td colspan="4" class="text-gray-600">Nicio persoana adaugata.</td>
				</tr>
				<tr v-for="(person, index) in people" :key="person.id_persoana">
					<td>{{ index + 1 }}</td>
					<td>{{ person.name }}</td>
					<td><span class="dot" :class="person.color"></span></td>
					<td>
						<v-btn icon small color="red" title="Sterge" @click="confirmDelete(person)">
							<v-icon small>mdi-delete</v-icon>
						</v-btn>
					</td>
				</tr>
			</tbody>
		</v-simple-table>

		<!-- Add -->
		<v-dialog v-model="showAdd" max-width="460px">
			<v-card class="p-8">
				<h6 class="text-xl font-bold mb-4">Adauga persoana</h6>
				<v-form ref="form">
					<v-text-field v-model="newPerson.name" label="Nume" :rules="[(v) => !!v || 'Obligatoriu']"
						outlined dense></v-text-field>
					<v-label>Culoare in calendar</v-label>
					<div class="flex flex-row flex-wrap gap-2 mt-2 mb-4">
						<v-chip v-for="color in colors" :key="color" small label
							:outlined="newPerson.color !== color" :disabled="usedColors.includes(color)"
							@click="newPerson.color = color">
							<span class="dot mr-1" :class="color"></span>{{ color }}
						</v-chip>
					</div>
					<p v-if="colorMissing" class="error--text text-sm">Alegeti o culoare.</p>
				</v-form>
				<div class="flex flex-row gap-2">
					<v-btn color="teal" class="uppercase" dark :loading="saving" @click="add">Salveaza</v-btn>
					<v-btn color="blue-grey" class="uppercase" dark @click="showAdd = false">Anuleaza</v-btn>
				</div>
			</v-card>
		</v-dialog>

		<!-- Delete -->
		<v-dialog v-model="showDelete" max-width="400px">
			<v-card v-if="toDelete" class="p-8">
				<h6 class="text-xl font-bold mb-4">Stergi definitiv pe {{ toDelete.name }}?</h6>
				<p class="text-sm text-gray-600">Dispare si din programarile in care era trecut.</p>
				<div class="flex flex-row gap-2">
					<v-btn color="red" class="uppercase" dark :loading="saving" @click="remove">Sterge</v-btn>
					<v-btn color="blue-grey" class="uppercase" dark @click="showDelete = false">Anuleaza</v-btn>
				</div>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { mapState } from "vuex";

// Colour classes understood by both Materialize and Vuetify (dots in the calendars).
const COLORS = [
	"red", "pink", "purple", "deep-purple", "indigo", "blue", "light-blue", "cyan",
	"teal", "green", "light-green", "lime", "amber", "orange", "deep-orange", "brown", "blue-grey",
];

// The people who can be assigned to bookings: production (tip=productie) or montage (tip=montaj).
export default {
	name: "SetariOameni",
	props: {
		tip: { type: String, required: true },
	},
	data() {
		return {
			colors: COLORS,
			showAdd: false,
			showDelete: false,
			toDelete: null,
			newPerson: { name: "", color: null },
			colorMissing: false,
			saving: false,
		};
	},
	computed: {
		...mapState("works", ["loadingPeople"]),
		people() {
			return this.$store.state.works.people[this.tip];
		},
		title() {
			return this.tip === "montaj" ? "Oameni echipa montaj" : "Oameni echipa productie";
		},
		// one colour per person within a team, so the calendar dots tell them apart
		usedColors() {
			return this.people.map((p) => p.color);
		},
	},
	methods: {
		openAdd() {
			this.newPerson = { name: "", color: null };
			this.colorMissing = false;
			this.showAdd = true;
			this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
		},
		async add() {
			this.colorMissing = !this.newPerson.color;
			if (!this.$refs.form.validate() || this.colorMissing) return;
			this.saving = true;
			try {
				await this.$store.dispatch("works/addPerson", { tip: this.tip, ...this.newPerson });
				this.showAdd = false;
			} catch (e) {
				// toast already shown by the store
			} finally {
				this.saving = false;
			}
		},
		confirmDelete(person) {
			this.toDelete = person;
			this.showDelete = true;
		},
		async remove() {
			this.saving = true;
			try {
				await this.$store.dispatch("works/deletePerson", { tip: this.tip, id: this.toDelete.id_persoana });
				this.showDelete = false;
			} catch (e) {
				// toast already shown by the store
			} finally {
				this.saving = false;
			}
		},
	},
	created() {
		this.$store.dispatch("works/fetchPeople", { tip: this.tip, force: true }).catch(() => {});
	},
};
</script>

<style scoped>
.dot {
	border-radius: 100px;
	width: 10px;
	height: 10px;
	display: inline-block;
}
</style>
